import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { getErrorTitle, WEBSITE_TEMPLATE_GALLERY_MAX_FILE_SIZE_IN_BYTES } from "../../../../constants/constants";
import { ReferenceType } from "../../../../models/document";
import { RoomEntry, RoomsSection } from "../../../../models/manageWebSite";
import { normalizePlainText } from "../../../../utils/securityUtils";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";

interface EditModalProps {
  data: RoomsSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const splitLines = (value: string) =>
  value
    .split("\n")
    .map((item) => normalizePlainText(item, { maxLength: 180 }))
    .filter(Boolean);

const createEmptyRoomEntry = (): RoomEntry => ({
  roomId: `room-${uuidv4()}`,
  name: "",
  spaceType: "",
  bedroomOption: "",
  bathroomOption: "",
  description: "",
  amenities: [],
  imageKeys: [],
  signedImageUrls: [],
});

const EditModal: FC<EditModalProps> = ({ isOpen, toggleModal, flag, setFlag, data, homeId }) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [roomEntries, setRoomEntries] = useState<RoomEntry[]>([]);
  const [pendingRoomFiles, setPendingRoomFiles] = useState<Record<string, File[]>>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { control, formState: { errors }, handleSubmit, reset } = useForm({
    defaultValues: {
      title: data?.title || "",
      description: data?.description || "",
      pricingNotes: data?.pricingNotes || "",
      enabled: data?.enabled || false,
    },
  });

  useEffect(() => {
    const sourceRoomEntries = Array.isArray(data?.roomEntries) ? (data?.roomEntries || []) : [];
    setRoomEntries(
      sourceRoomEntries.length > 0
        ? sourceRoomEntries.map((room) => ({
          ...room,
          amenities: Array.isArray(room.amenities) ? room.amenities : [],
          imageKeys: Array.isArray(room.imageKeys) ? room.imageKeys : [],
          signedImageUrls: Array.isArray(room.signedImageUrls) ? room.signedImageUrls : [],
        }))
        : [createEmptyRoomEntry()]
    );
    setPendingRoomFiles({});
    reset({
      title: data?.title || "",
      description: data?.description || "",
      pricingNotes: data?.pricingNotes || "",
      enabled: data?.enabled || false,
    });
  }, [data, isOpen, reset]);

  const handleFileChange = (roomId: string, event: any) => {
    const files = Array.isArray(event.files) ? event.files : [];
    const currentRoom = roomEntries.find((room) => room.roomId === roomId);
    const remainingSlots = Math.max(0, 5 - ((currentRoom?.imageKeys || []).length));
    if (files.length > remainingSlots) {
      setErrorMessage(`You can upload up to 5 photos per room. ${remainingSlots} slot${remainingSlots === 1 ? "" : "s"} remaining for this room.`);
    } else {
      setErrorMessage(null);
    }
    setPendingRoomFiles((prev) => ({ ...prev, [roomId]: files.slice(0, remainingSlots) }));
  };

  const updateRoomEntry = (roomId: string, field: keyof RoomEntry, value: string | string[]) => {
    setRoomEntries((prev) =>
      prev.map((room) => (room.roomId === roomId ? { ...room, [field]: value } : room))
    );
  };

  const addRoomEntry = () => setRoomEntries((prev) => [...prev, createEmptyRoomEntry()]);

  const removeRoomEntry = (roomId: string) => {
    setRoomEntries((prev) => prev.filter((room) => room.roomId !== roomId));
    setPendingRoomFiles((prev) => {
      const next = { ...prev };
      delete next[roomId];
      return next;
    });
  };

  const removeRoomImage = (roomId: string, index: number) => {
    setRoomEntries((prev) =>
      prev.map((room) => {
        if (room.roomId !== roomId) {
          return room;
        }
        return {
          ...room,
          imageKeys: room.imageKeys.filter((_, currentIndex) => currentIndex !== index),
          signedImageUrls: (room.signedImageUrls || []).filter((_, currentIndex) => currentIndex !== index),
        };
      })
    );
  };

  const uploadImages = async () => {
    const uploadedRoomEntries = await Promise.all(
      roomEntries.map(async (room) => {
        const pendingFiles = pendingRoomFiles[room.roomId] || [];
        if (!pendingFiles.length) {
          return room;
        }

        const remainingSlots = Math.max(0, 5 - (room.imageKeys?.length || 0));
        const filesToUpload = pendingFiles.slice(0, remainingSlots);
        const newKeys = filesToUpload.map(() => `website/default_template/${homeId}/draft/ROOMS-${room.roomId}-${uuidv4()}`);
        const presignedUrls = await Promise.all(
          newKeys.map((objectKey) => backendAPIStore.getPresignedUrlForUpload(homeId!, ReferenceType.BUSINESS_HOME, objectKey))
        );
        await Promise.all(presignedUrls.map((presignedUrl, index) => backendAPIStore.uploadFileToS3(presignedUrl, filesToUpload[index])));

        return {
          ...room,
          imageKeys: [...(room.imageKeys || []), ...newKeys].slice(0, 5),
        };
      })
    );

    return uploadedRoomEntries;
  };

  const onSubmit = async (formData: any) => {
    setErrorMessage(null);
    setLoading(true);
    try {
      await backendAPIStore.init();
      const uploadedRoomEntries = await uploadImages();
      const normalizedRoomEntries = uploadedRoomEntries
        .map((room) => ({
          ...room,
          name: normalizePlainText(room.name, { maxLength: 120 }),
          spaceType: normalizePlainText(room.spaceType, { maxLength: 80 }),
          bedroomOption: normalizePlainText(room.bedroomOption, { maxLength: 120 }),
          bathroomOption: normalizePlainText(room.bathroomOption, { maxLength: 120 }),
          description: normalizePlainText(room.description, { multiline: true, maxLength: 500 }),
          amenities: Array.isArray(room.amenities) ? room.amenities.map((item) => normalizePlainText(item, { maxLength: 120 })).filter(Boolean) : [],
          imageKeys: Array.isArray(room.imageKeys) ? room.imageKeys.slice(0, 5) : [],
        }))
        .filter((room) => room.name || room.spaceType || room.description || room.imageKeys.length > 0);

      await backendAPIStore.updateTemplateSection({
        ...data,
        title: normalizePlainText(formData.title, { maxLength: 120 }),
        description: normalizePlainText(formData.description, { multiline: true, maxLength: 1200 }),
        pricingNotes: normalizePlainText(formData.pricingNotes, { multiline: true, maxLength: 500 }),
        roomOptions: normalizedRoomEntries.map((room) => `${room.name}${room.spaceType ? ` - ${room.spaceType}` : ""}`),
        bathroomOptions: normalizedRoomEntries.map((room) => room.bathroomOption).filter(Boolean),
        amenities: Array.from(new Set(normalizedRoomEntries.flatMap((room) => room.amenities || []))),
        enabled: !!formData.enabled,
        sectionImg: normalizedRoomEntries.flatMap((room) => room.imageKeys || []).slice(0, 20),
        roomEntries: normalizedRoomEntries,
        modifiedAt: new Date().toISOString(),
      });
      toggleModal(false);
      setFlag(!flag);
      setPendingRoomFiles({});
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: "Unable to save the rooms page.", life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const footerContent = renderDialogFooter({
    errorMessage,
    loading,
    onCancel: () => toggleModal(false),
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Rooms Page",
  });

  return (
    <Dialog
      visible={isOpen}
      modal
      header={renderSurfaceHeader("Rooms Page", "Manage room offerings, bathroom setup, amenities, and photos for the optional rooms page.", "pi pi-home")}
      footer={footerContent}
      style={appDialogStyle}
      headerStyle={appDialogHeaderStyle}
      contentStyle={appDialogContentStyle}
      onHide={() => toggleModal(false)}
    >
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#185da2" }}>Optional page controls</div>
        <div className="mt-2" style={appMutedTextStyle}>Turn this page on only when you are ready for it to appear in the public website navigation.</div>
      </div>
      <form>
        <div style={appFormSectionStyle}>
          <div className="flex align-items-center gap-2">
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Checkbox inputId="roomsEnabled" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />
              )}
            />
            <label htmlFor="roomsEnabled" style={{ fontWeight: 600 }}>Add Rooms page into the website</label>
          </div>
        </div>
        {[
          { name: "title", label: "Page Title", rows: 4 },
          { name: "description", label: "Description", rows: 6 },
          { name: "pricingNotes", label: "Pricing Notes", rows: 4 },
        ].map(({ name, label, rows }) => (
          <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} key={name}>
            <label htmlFor={name}><strong>{label}</strong></label>
            <Controller
              name={name as any}
              control={control}
              render={({ field }) => <InputTextarea id={name} {...field} rows={rows} style={{ width: "100%" }} className={errors[name as keyof typeof errors] ? "p-invalid" : ""} />}
            />
          </div>
        ))}
        <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
          <div className="flex align-items-center justify-content-between gap-3 mb-3">
            <div>
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Room entries</div>
              <div className="mt-1" style={appMutedTextStyle}>Add named rooms or shared spaces like Room 1, Living Room, Kitchen, or Dining Area. Each room can have up to 5 photos.</div>
            </div>
            <Button type="button" label="Add Room" severity="warning" onClick={addRoomEntry} />
          </div>

          <div className="flex flex-column gap-3">
            {roomEntries.map((room, roomIndex) => (
              <div key={room.roomId} style={{ border: "1px solid #dbe7f3", borderRadius: "20px", padding: "1rem", background: "#ffffff" }}>
                <div className="flex align-items-center justify-content-between gap-3 mb-3">
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Room {roomIndex + 1}</div>
                  {roomEntries.length > 1 ? (
                    <Button type="button" icon="pi pi-trash" rounded outlined severity="danger" onClick={() => removeRoomEntry(room.roomId)} />
                  ) : null}
                </div>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label><strong>Room Name</strong></label>
                    <InputText value={room.name} onChange={(e) => updateRoomEntry(room.roomId, "name", e.target.value)} placeholder="Room 1, Living Room, Kitchen" className="w-full mt-2" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label><strong>Space Type</strong></label>
                    <InputText value={room.spaceType} onChange={(e) => updateRoomEntry(room.roomId, "spaceType", e.target.value)} placeholder="Bedroom, Shared space, Kitchen" className="w-full mt-2" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label><strong>Bedroom Option</strong></label>
                    <InputText value={room.bedroomOption} onChange={(e) => updateRoomEntry(room.roomId, "bedroomOption", e.target.value)} placeholder="Private bedroom, Shared bedroom" className="w-full mt-2" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label><strong>Bathroom Option</strong></label>
                    <InputText value={room.bathroomOption} onChange={(e) => updateRoomEntry(room.roomId, "bathroomOption", e.target.value)} placeholder="Private bathroom, Shared bathroom" className="w-full mt-2" />
                  </div>
                  <div className="col-12">
                    <label><strong>Description</strong></label>
                    <InputTextarea value={room.description} onChange={(e) => updateRoomEntry(room.roomId, "description", e.target.value)} rows={3} className="w-full mt-2" />
                  </div>
                  <div className="col-12">
                    <label><strong>Amenities</strong></label>
                    <InputTextarea value={(room.amenities || []).join("\n")} onChange={(e) => updateRoomEntry(room.roomId, "amenities", splitLines(e.target.value))} rows={4} className="w-full mt-2" />
                  </div>
                  <div className="col-12">
                    <div className="mb-2"><strong>Room Photos</strong></div>
                    <FileUpload
                      multiple
                      mode="advanced"
                      name={`roomImages-${room.roomId}`}
                      onSelect={(event) => handleFileChange(room.roomId, event)}
                      accept="image/*"
                      maxFileSize={WEBSITE_TEMPLATE_GALLERY_MAX_FILE_SIZE_IN_BYTES}
                      emptyTemplate={<p className="m-0">Upload up to 5 photos for this room.</p>}
                    />
                    <div className="mt-2" style={appMutedTextStyle}>
                      Existing photos: {(room.signedImageUrls || []).length} / 5. Pending uploads: {(pendingRoomFiles[room.roomId] || []).length}
                    </div>
                    <div className="grid mt-3">
                      {(room.signedImageUrls || []).map((image, index) => (
                        <div className="col-6 md:col-3" key={`${image}-${index}`}>
                          <div style={{ position: "relative" }}>
                            <div style={{ minHeight: "130px", borderRadius: "18px", background: `url(${image}) center/cover no-repeat`, border: "1px solid #dbe7f3" }} />
                            <Button icon="pi pi-times" rounded outlined severity="danger" style={{ position: "absolute", top: 8, right: 8 }} onClick={(event) => { event.preventDefault(); removeRoomImage(room.roomId, index); }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default EditModal;
